import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Lucide icons removed for build stability
const ChevronRight = () => <span>→</span>;
const CheckCircle2 = () => <span>✅</span>;
const Trophy = () => <span>🏆</span>;
const TrendingUp = () => <span>📈</span>;
const Users = () => <span>👥</span>;
const Globe = () => <span>🌐</span>;
const Clock = () => <span>🕒</span>;
const ShieldCheck = () => <span>🛡️</span>;
const ArrowRight = () => <span>→</span>;
const Menu = () => <span>☰</span>;
const X = () => <span>✕</span>;

const LandingPage = ({ onStartChallenge }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const tiers = [
    { id: '10k', label: 'Starter', price: 19, initial: '10,000', target: '1,000', drawdown: '1,000', daily: '500', split: '80%', featured: false },
    { id: '50k', label: 'Professional', price: 59, initial: '50,000', target: '5,000', drawdown: '5,000', daily: '2,500', split: '80%', featured: true },
    { id: '100k', label: 'Elite', price: 99, initial: '100,000', target: '10,000', drawdown: '10,000', daily: '5,000', split: '80%', featured: false },
  ];

  const payouts = [
    { name: 'alex_trader', amount: '$4,200', date: '2h ago', flag: '🇺🇸' },
    { name: 'crypto_king', amount: '$12,500', date: '5h ago', flag: '🇬🇧' },
    { name: 'zenith_pro', amount: '$8,100', date: '1d ago', flag: '🇩🇪' },
    { name: 'max_gainz', amount: '$15,000', date: '2d ago', flag: '🇦🇺' },
  ];

  const leaderboard = [
    { rank: 1, name: 'satoshi_v2', return: '42.5%', equity: '$142,500' },
    { rank: 2, name: 'alpha_whale', return: '31.2%', equity: '$131,200' },
    { rank: 3, name: 'trading_god', return: '28.7%', equity: '$128,700' },
    { rank: 4, name: 'bull_run_99', return: '21.1%', equity: '$121,100' },
  ];

  return (
    <div className="bg-[#08090A] text-[#FAFAFA] min-h-screen font-sans selection:bg-[#C6F94E] selection:text-black">
      {/* Sticky Nav */}
      <nav className="sticky top-0 z-50 w-full border-b border-[#1F2124] bg-[#08090A]/80 backdrop-blur-md">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
            <div className="w-8 h-8 bg-[#C6F94E] rounded-lg flex items-center justify-center">
              <span className="text-black font-black text-xl">Z</span>
            </div>
            <span className="font-bold text-xl tight-spacing tracking-tighter">ZENITH <span className="text-[#8A8F98] font-medium">PROP</span></span>
          </div>

          <div className="hidden md:flex items-center space-x-8 text-sm font-medium text-[#8A8F98]">
            <a href="#challenges" className="hover:text-white transition-colors">Challenges</a>
            <a href="#rules" className="hover:text-white transition-colors">Rules</a>
            <a href="#payouts" className="hover:text-white transition-colors">Payouts</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
          </div>

          <div className="hidden md:block">
            <button
              onClick={() => navigate('/login')}
              className="accent-btn px-5 py-2 rounded-full text-sm transition-all hover:scale-105 active:scale-95"
            >
              Start Challenge
            </button>
          </div>

          <button className="md:hidden" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40 bg-[#08090A] flex flex-col items-center justify-center space-y-8 text-2xl font-bold">
          <a href="#challenges" onClick={() => setIsMenuOpen(false)}>Challenges</a>
          <a href="#rules" onClick={() => setIsMenuOpen(false)}>Rules</a>
          <a href="#payouts" onClick={() => setIsMenuOpen(false)}>Payouts</a>
          <a href="#faq" onClick={() => setIsMenuOpen(false)}>FAQ</a>
          <button onClick={() => { navigate('/login'); setIsMenuOpen(false); }} className="accent-btn px-8 py-4 rounded-full text-xl">Start Challenge</button>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative pt-24 pb-32 px-6 overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-20 pointer-events-none" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#C6F94E]/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-[1200px] mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <h1 className="text-[56px] md:text-[72px] leading-[1.1] font-bold tight-spacing tracking-tighter">
              Trade our <span className="text-[#C6F94E]">capital.</span><br />
              Keep <span className="text-[#C6F94E]">90%.</span>
            </h1>
            <p className="text-xl text-[#8A8F98] max-w-lg leading-relaxed">
              The most transparent and high-performance prop firm for crypto-native traders.
              Zero hidden rules. Instant funding. Institutional grade tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/login')}
                className="accent-btn px-8 py-4 rounded-full text-lg flex items-center justify-center space-x-2 group"
              >
                <span>Start Challenge</span>
                <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="ghost-btn px-8 py-4 rounded-full text-lg font-medium">
                View Rules
              </button>
            </div>
          </div>

          <div className="relative">
            {/* Floating Dashboard Preview */}
            <div className="glass-card p-4 relative z-10 animate-in fade-in slide-in-from-bottom-8 duration-1000">
              <div className="flex items-center justify-between mb-6 border-b border-[#1F2124] pb-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="text-xs font-mono text-[#8A8F98]">ACCOUNT: ZENITH-PRO-882</div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-[#08090A] rounded-lg border border-[#1F2124]">
                  <div className="text-xs text-[#8A8F98] uppercase font-bold">Equity</div>
                  <div className="text-2xl font-mono font-bold text-[#C6F94E]">$112,450.22</div>
                </div>
                <div className="p-4 bg-[#08090A] rounded-lg border border-[#1F2124]">
                  <div className="text-xs text-[#8A8F98] uppercase font-bold">Total Profit</div>
                  <div className="text-2xl font-mono font-bold text-[#2ED573]">+$12,450.22</div>
                </div>
              </div>
              <div className="h-40 w-full bg-[#08090A] rounded-lg relative overflow-hidden border border-[#1F2124]">
                {/* Mock Chart Line */}
                <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                  <path
                    d="M0 80 L50 70 L100 90 L150 40 L200 60 L250 20 L300 30 L350 10 L400 40"
                    fill="none"
                    stroke="#C6F94E"
                    strokeWidth="3"
                    className="animate-draw"
                  />
                </svg>
                <div className="absolute top-2 right-2 text-[10px] font-mono text-[#8A8F98]">LIVE FEED ACTIVE</div>
              </div>
            </div>
            {/* Decorative glow behind card */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[#C6F94E]/20 rounded-full blur-[80px] -z-10" />
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-12 border-y border-[#1F2124] bg-[#0C0D0F]">
        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { label: 'Total Payouts', val: '$12.4M', icon: <TrendingUp size={20}/> },
            { label: 'Funded Traders', val: '1,240+', icon: <Users size={20}/> },
            { label: 'Avg Payout Time', val: '24 Hours', icon: <Clock size={20}/> },
            { label: 'Countries', val: '42+', icon: <Globe size={20}/> },
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center text-center space-y-1">
              <div className="text-[#C6F94E] mb-2">{item.icon}</div>
              <div className="text-3xl font-mono font-bold">{item.val}</div>
              <div className="text-xs text-[#8A8F98] uppercase tracking-widest font-medium">{item.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Challenge Tiers */}
      <section id="challenges" className="py-32 px-6">
        <div className="max-w-[1200px] mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold tight-spacing tracking-tighter mb-4">Choose Your <span className="text-[#C6F94E]">Challenge</span></h2>
          <p className="text-[#8A8F98] max-w-2xl mx-auto">Select the account size that fits your trading style. All challenges include institutional tools and full support.</p>
        </div>

        <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={`glass-card p-8 flex flex-col transition-all duration-300 ${tier.featured ? 'border-[#C6F94E] scale-105 ring-1 ring-[#C6F94E]/30' : ''}`}
            >
              <div className="flex justify-between items-start mb-8">
                <div>
                  <div className="text-sm font-bold text-[#8A8F98] uppercase mb-1">{tier.label}</div>
                  <div className="text-4xl font-mono font-bold">${tier.initial}</div>
                </div>
                <div className="px-3 py-1 rounded-full bg-[#C6F94E]/10 text-[#C6F94E] text-xs font-bold">
                  ${tier.price} Entry
                </div>
              </div>

              <div className="space-y-4 mb-8 flex-1">
                <div className="flex justify-between text-sm border-b border-[#1F2124] pb-2">
                  <span className="text-[#8A8F98]">Profit Target</span>
                  <span className="font-mono font-bold">${tier.target}</span>
                </div>
                <div className="flex justify-between text-sm border-b border-[#1F2124] pb-2">
                  <span className="text-[#8A8F98]">Max Daily Loss</span>
                  <span className="font-mono font-bold text-red-400">${tier.daily}</span>
                </div>
                <div className="flex justify-between text-sm border-b border-[#1F2124] pb-2">
                  <span className="text-[#8A8F98]">Max Drawdown</span>
                  <span className="font-mono font-bold text-red-400">${tier.drawdown}</span>
                </div>
                <div className="flex justify-between text-sm border-b border-[#1F2124] pb-2">
                  <span className="text-[#8A8F98]">Profit Split</span>
                  <span className="font-mono font-bold text-[#C6F94E]">{tier.split}</span>
                </div>
              </div>

              <button
                onClick={() => navigate('/login')}
                className={`w-full py-4 rounded-xl font-bold transition-all ${tier.featured ? 'accent-btn' : 'ghost-btn'}`}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* How it Works */}
      <section className="py-32 px-6 bg-[#0C0D0F]">
        <div className="max-w-[1200px] mx-auto text-center mb-20">
          <h2 className="text-4xl font-bold tight-spacing tracking-tighter mb-4">How It <span className="text-[#C6F94E]">Works</span></h2>
          <p className="text-[#8A8F98]">Three simple steps to professional funding.</p>
        </div>

        <div className="max-w-[1200px] mx-auto grid md:grid-cols-3 gap-12 relative">
          {/* Connecting Line (Desktop) */}
          <div className="hidden md:block absolute top-1/2 left-0 w-full h-[1px] bg-[#1F2124] -z-10" />

          {[
            { step: '01', title: 'The Challenge', desc: 'Prove your skills. Hit the profit target without hitting the drawdown limits.' },
            { step: '02', title: 'Verification', desc: 'A quick audit of your trading style to ensure consistency and risk management.' },
            { step: '03', title: 'Funding', desc: 'Get your funded account. Trade our capital and keep up to 90% of the profits.' },
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-[#121316] border-2 border-[#C6F94E] flex items-center justify-center font-mono font-bold text-[#C6F94E] z-10">
                {item.step}
              </div>
              <h3 className="text-xl font-bold">{item.title}</h3>
              <p className="text-[#8A8F98] text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trading Rules */}
      <section id="rules" className="py-32 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div className="max-w-xl">
              <h2 className="text-4xl font-bold tight-spacing tracking-tighter mb-4">Trading <span className="text-[#C6F94E]">Rules</span></h2>
              <p className="text-[#8A8F98]">Fair and transparent rules. No hidden traps. Just professional trading.</p>
            </div>
            <div className="flex items-center space-x-2 text-xs font-mono text-green-400 bg-green-400/10 px-3 py-1 rounded-full">
              <ShieldCheck size={14} />
              <span>REGULATED RISK ENGINE</span>
            </div>
          </div>

          <div className="glass-card overflow-hidden p-0">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#1F2124] text-[#8A8F98] text-xs uppercase tracking-widest font-bold">
                  <th className="p-4">Rule</th>
                  <th className="p-4">Limit</th>
                  <th className="p-4">Description</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {[
                  { rule: 'Max Daily Loss', limit: '5%', desc: 'Calculated from the balance at the start of the trading day.' },
                  { rule: 'Max Overall Drawdown', limit: '10%', desc: 'The total equity cannot drop below 10% of the initial balance.' },
                  { rule: 'Minimum Trading Days', limit: '5 Days', desc: 'Maintain activity for at least 5 days to qualify for funding.' },
                  { rule: 'Maximum Leverage', limit: '1:100', desc: 'Standard institutional leverage applied to all assets.' },
                  { rule: 'News Trading', limit: 'Allowed', desc: 'Trade high-impact news without restrictions.' },
                ].map((row, i) => (
                  <tr key={i} className={`${i % 2 === 0 ? 'bg-transparent' : 'bg-[#1F2124]/30'} border-b border-[#1F2124] transition-colors hover:bg-[#1F2124]/60`}>
                    <td className="p-4 font-medium">{row.rule}</td>
                    <td className="p-4 font-mono font-bold text-[#C6F94E]">{row.limit}</td>
                    <td className="p-4 text-[#8A8F98]">{row.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Payout Proof */}
      <section id="payouts" className="py-32 px-6 bg-[#0C0D0F]">
        <div className="max-w-[1200px] mx-auto text-center mb-16">
          <h2 className="text-4xl font-bold tight-spacing tracking-tighter mb-4">Proof of <span className="text-[#C6F94E]">Payouts</span></h2>
          <p className="text-[#8A8F98]">Real traders, real money. We pay on time, every time.</p>
        </div>

        <div className="max-w-2xl mx-auto space-y-3">
          {payouts.map((p, i) => (
            <div key={i} className="glass-card p-4 flex items-center justify-between animate-in slide-in-from-right-8 duration-500" style={{ animationDelay: `${i * 100}ms` }}>
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{p.flag}</span>
                <div className="text-left">
                  <div className="font-bold text-sm">{p.name}</div>
                  <div className="text-xs text-[#8A8F98] font-mono">{p.date}</div>
                </div>
              </div>
              <div className="text-xl font-mono font-bold text-[#2ED573]">+{p.amount}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Leaderboard */}
      <section className="py-32 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold tight-spacing tracking-tighter mb-4">Hall of <span className="text-[#C6F94E]">Fame</span></h2>
            <p className="text-[#8A8F98]">Top performers of the month.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="glass-card p-6 space-y-4">
              {leaderboard.map((trader, i) => (
                <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-[#08090A] border border-[#1F2124]">
                  <div className="flex items-center space-x-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${i === 0 ? 'bg-[#C6F94E] text-black' : 'bg-[#1F2124] text-white'}`}>
                      {trader.rank}
                    </div>
                    <span className="font-bold">{trader.name}</span>
                  </div>
                  <div className="flex items-center space-x-6">
                    <div className="text-right">
                      <div className="text-xs text-[#8A8F98] uppercase font-bold">Return</div>
                      <div className="font-mono font-bold text-[#2ED573]">{trader.return}</div>
                    </div>
                    <div className="text-right w-24">
                      <div className="text-xs text-[#8A8F98] uppercase font-bold">Equity</div>
                      <div className="font-mono font-bold">{trader.equity}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-6">
              <div className="p-8 glass-card border-l-4 border-l-[#C6F94E]">
                <Trophy className="text-[#C6F94E] mb-4" size={40} />
                <h3 className="text-2xl font-bold mb-2">Want to be here?</h3>
                <p className="text-[#8A8F98] mb-6">Join 1,000+ professional traders and get funded today.</p>
                <button onClick={() => navigate('/login')} className="accent-btn px-6 py-3 rounded-full w-full">Join the Elite</button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 glass-card text-center">
                  <div className="text-3xl font-mono font-bold text-[#C6F94E]">90%</div>
                  <div className="text-xs text-[#8A8F98] uppercase font-bold">Profit Split</div>
                </div>
                <div className="p-6 glass-card text-center">
                  <div className="text-3xl font-mono font-bold text-[#C6F94E]">0%</div>
                  <div className="text-xs text-[#8A8F98] uppercase font-bold">Hidden Fees</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-32 px-6 bg-[#0C0D0F]">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-4xl font-bold text-center tight-spacing tracking-tighter mb-16">Common <span className="text-[#C6F94E]">Questions</span></h2>
          <div className="space-y-4">
            {[
              { q: 'How long does funding take?', a: 'Once you pass the challenge and verification, your funded account is typically active within 24 hours.' },
              { q: 'Can I trade on mobile?', a: 'Yes, our platform is fully responsive and supports all modern mobile browsers.' },
              { q: 'What happens if I fail a challenge?', a: 'You can reset your account or purchase a new challenge at any time from the dashboard.' },
              { q: 'Is there a time limit to pass?', a: 'No. We believe in quality trading over rushed gambling. Take your time to hit the target.' },
            ].map((item, i) => (
              <details key={i} className="group glass-card p-4 cursor-pointer">
                <summary className="flex justify-between items-center font-bold list-none">
                  {item.q}
                  <ChevronRight size={20} className="group-open:rotate-90 transition-transform text-[#8A8F98]" />
                </summary>
                <p className="mt-4 text-[#8A8F98] leading-relaxed text-sm">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 px-6 border-t border-[#1F2124]">
        <div className="max-w-[1200px] mx-auto grid md:grid-cols-4 gap-12">
          <div className="col-span-2 space-y-6">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-[#C6F94E] rounded-lg flex items-center justify-center">
                <span className="text-black font-black text-xl">Z</span>
              </div>
              <span className="font-bold text-xl tight-spacing tracking-tighter">ZENITH <span className="text-[#8A8F98] font-medium">PROP</span></span>
            </div>
            <p className="text-[#8A8F98] max-w-sm text-sm leading-relaxed">
              The next generation of prop trading. Empowering traders with institutional capital and an unfair advantage.
            </p>
          </div>
          <div className="space-y-4">
            <div className="font-bold uppercase text-xs tracking-widest text-white">Platform</div>
            <div className="flex flex-col space-y-2 text-sm text-[#8A8F98]">
              <a href="#challenges" className="hover:text-white transition-colors">Challenges</a>
              <a href="#rules" className="hover:text-white transition-colors">Rules</a>
              <a href="#payouts" className="hover:text-white transition-colors">Payouts</a>
            </div>
          </div>
          <div className="space-y-4">
            <div className="font-bold uppercase text-xs tracking-widest text-white">Company</div>
            <div className="flex flex-col space-y-2 text-sm text-[#8A8F98]">
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
        </div>
        <div className="max-w-[1200px] mx-auto mt-20 pt-8 border-t border-[#1F2124] text-center">
          <p className="text-[10px] text-[#8A8F98] leading-relaxed max-w-3xl mx-auto">
            Risk Warning: Trading involves significant risk of loss. Prop trading is a skill-based evaluation.
            Past performance of funded traders is not indicative of future results.
            © 2026 Zenith Prop. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
